import type { CSSProperties } from "react";
import { formatKr } from "@/lib/format";
import type { WeekFinancials } from "@/lib/weekFinancials";
import { Card } from "./Card";

interface Props {
  financials: WeekFinancials;
  title?: string;
  style?: CSSProperties;
  "data-testid"?: string;
}

interface RowSpec {
  key: string;
  label: string;
  value: number | null;
  testId: string;
}

export function WeekSummaryCard({
  financials,
  title,
  style,
  "data-testid": testId,
}: Props) {
  const rows: RowSpec[] = [
    {
      key: "earned",
      label: "Tjent",
      value: financials.earned,
      testId: "summary-row-earned",
    },
    {
      key: "invested",
      label: "Investert",
      value: financials.investedDiff,
      testId: "summary-row-invested",
    },
    {
      key: "fluctuation",
      label: "Verdiendring investeringer",
      value: financials.fluctuation,
      testId: "summary-row-fluctuation",
    },
    {
      key: "spent",
      label: "Forbruk",
      value: financials.spent,
      testId: "summary-row-spent",
    },
    {
      key: "total",
      label: "Endring i formue",
      value: financials.totalChange,
      testId: "summary-row-total",
    },
  ];

  return (
    <Card
      padding={0}
      data-testid={testId ?? "week-summary-card"}
      style={{ overflow: "hidden", ...style }}
    >
      {title && (
        <div
          style={{
            padding: "12px 16px 8px",
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.7,
            color: "var(--ink-3)",
            borderBottom: "1px solid var(--line)",
          }}
        >
          {title}
        </div>
      )}
      {rows.map((row, i) => (
        <SummaryRow
          key={row.key}
          label={row.label}
          value={row.value}
          testId={row.testId}
          isLast={i === rows.length - 1}
        />
      ))}
    </Card>
  );
}

function SummaryRow({
  label,
  value,
  testId,
  isLast,
}: {
  label: string;
  value: number | null;
  testId: string;
  isLast: boolean;
}) {
  const isMissing = value == null;
  const isPositive = !isMissing && value > 0;
  const isNegative = !isMissing && value < 0;
  const color = isMissing
    ? "var(--ink-3)"
    : isPositive
      ? "var(--good)"
      : isNegative
        ? "var(--bad)"
        : "var(--ink)";
  const display = isMissing
    ? "—"
    : isPositive
      ? `+${formatKr(value)}`
      : isNegative
        ? `−${formatKr(Math.abs(value))}`
        : formatKr(0);

  return (
    <div
      data-testid={testId}
      data-sign={
        isMissing ? "missing" : isPositive ? "pos" : isNegative ? "neg" : "zero"
      }
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderBottom: isLast ? "none" : "1px solid var(--line)",
        fontSize: 14,
      }}
    >
      <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>{label}</span>
      <span
        className="num"
        data-testid={`${testId}-value`}
        style={{ color, fontWeight: 700 }}
      >
        {display}
      </span>
    </div>
  );
}
